import { type EcosystemService, findService } from "@iec-id/shared/constants";
import type { OnboardingState } from "@iec-id/shared/contracts/profile";
import { type AuthSearch, resolveService } from "@/lib/auth-flow";

/**
 * Сервис-инициатор, запомненный на время вкладки.
 *
 * Query может потеряться на середине пути: человек открыл `/onboarding` из
 * закладки, обновил страницу без параметров, вернулся «назад» из письма. Без
 * этого запаса финальная кнопка в таких случаях превращалась в «Открыть мой
 * IEC ID», хотя человек пришёл из StudyHub и ждёт возврата туда.
 *
 * `sessionStorage`, а не `localStorage`: это контекст ОДНОГО входа. Пришедший
 * завтра напрямую не должен получить кнопку в сервис, из которого приходил
 * неделю назад.
 */
const STORAGE_KEY = "iec-id.onboarding.return-service";

function remember(serviceId: string): void {
	try {
		sessionStorage.setItem(STORAGE_KEY, serviceId);
	} catch {
		// Приватный режим Safari. Кнопка просто останется общей — это подпись,
		// а не право доступа.
	}
}

function recall(): EcosystemService | undefined {
	try {
		const stored = sessionStorage.getItem(STORAGE_KEY);
		return stored ? findService(stored) : undefined;
	} catch {
		return undefined;
	}
}

/**
 * Куда человек вернётся после мастера: сервис из состояния мастера (его
 * запомнил сервер при первом входе), иначе — тот, что привёл на форму входа.
 * Витрина IEC ID — последний вариант, не первый.
 *
 * Вынесено из `DoneStep`, потому что тот же ответ нужен слайдам Д13: подпись
 * их последней кнопки — то же самое «Перейти в StudyHub».
 */
export function returnServiceOf(
	state: OnboardingState,
	search: AuthSearch,
): EcosystemService | undefined {
	const fromState = state.returnServiceId ? findService(state.returnServiceId) : undefined;
	const found = fromState ?? resolveService(search);
	// Витрина IEC ID — это «никакого сервиса»: запоминать её незачем, а кнопка
	// для неё и так общая.
	if (found && found.id !== "iec-id") {
		remember(found.id);
		return found;
	}
	return found ?? recall();
}
